import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, ClockIcon } from '@heroicons/react/24/outline';
import { resources, resourceCategories } from '../../content/resources';
import SunRaysHero from '../Services/SunRaysHero';

function Eyebrow({ children, dark = false }) {
	return (
		<p
			className={`text-xs font-semibold uppercase tracking-[0.18em] ${
				dark ? 'text-primary-400' : 'text-primary-600'
			}`}
		>
			<span className='inline-block w-8 h-px align-middle mr-3 bg-current opacity-60' />
			{children}
		</p>
	);
}

// Resource Center index (idea #5). Articles are grouped by category. The whole
// point: every question Gabe answers three times becomes a page here.
export default function Resources() {
	const grouped = Object.keys(resourceCategories).map((key) => ({
		key,
		label: resourceCategories[key],
		items: resources.filter((r) => r.category === key),
	}));

	return (
		<div className='bg-surface text-ink-900 antialiased'>
			<section className='relative isolate overflow-hidden border-b border-ink-100'>
				<SunRaysHero seed={8} />
				<div className='mx-auto max-w-7xl px-6 py-20 sm:py-28 lg:px-8'>
					<Eyebrow>Resource center</Eyebrow>
					<h1 className='font-display mt-5 text-5xl font-medium tracking-tight text-ink-900 sm:text-6xl leading-[1.05]'>
						Straight answers to the questions I get most.
					</h1>
					<p className='mt-6 max-w-2xl text-lg leading-8 text-ink-600'>
						Equipment comparisons, incentives, and how solar actually works —
						written the way I&apos;d explain it at your kitchen table.
					</p>
				</div>
			</section>

			<section className='bg-white py-20 sm:py-28'>
				<div className='mx-auto max-w-7xl space-y-20 px-6 lg:px-8'>
					{grouped.map((group) =>
						group.items.length === 0 ? null : (
							<div key={group.key}>
								<Eyebrow>{group.label}</Eyebrow>
								<div className='mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3'>
									{group.items.map((r) => (
										<Link
											key={r.id}
											to={`/resources/${r.id}`}
											className='group sg-card sg-card-interactive flex flex-col p-7'
										>
											<h2 className='font-display text-xl font-medium text-ink-900'>
												{r.title}
											</h2>
											<p className='mt-3 flex-1 text-sm leading-6 text-ink-500'>
												{r.excerpt}
											</p>
											<div className='mt-6 flex items-center justify-between'>
												<span className='inline-flex items-center gap-1 text-sm font-semibold text-primary-600 group-hover:gap-2 transition-all'>
													Read <ArrowRightIcon className='h-4 w-4' />
												</span>
												{r.readingTime && (
													<span className='inline-flex items-center gap-1 text-xs text-ink-400'>
														<ClockIcon className='h-4 w-4' />
														{r.readingTime}
													</span>
												)}
											</div>
										</Link>
									))}
								</div>
							</div>
						),
					)}
				</div>
			</section>

			<section className='bg-surface border-t border-ink-100 py-20'>
				<div className='mx-auto max-w-3xl px-6 text-center lg:px-8'>
					<h2 className='font-display text-3xl font-medium tracking-tight text-ink-900 sm:text-4xl leading-tight'>
						Don&apos;t see your question?
					</h2>
					<p className='mt-4 text-base leading-7 text-ink-500'>
						Ask it directly — I personally review every question, no pressure and
						no obligation.
					</p>
					<div className='mt-8'>
						<Link
							to='/ask-gabe'
							className='inline-flex items-center gap-2 rounded-full bg-primary-500 px-8 py-4 text-base font-semibold text-white shadow-lg shadow-primary-500/25 hover:bg-primary-600 transition-all'
						>
							Ask Gabe a solar question
							<ArrowRightIcon className='h-5 w-5' />
						</Link>
					</div>
				</div>
			</section>
		</div>
	);
}
